import type { EntityBatchSyncStatus } from './entityMutationSync'

/**
 * Returns true when the Datatable production-write sync surface has unfinished
 * work that should be retried automatically on a network/visibility reconnect.
 *
 * This is intentionally side-effect free so it can be unit tested without the
 * Datatable screen mounted. The caller (Datatable) is responsible for calling
 * the actual retry helper when this returns true.
 *
 * Decision rules:
 *  - offline navigators never trigger retries (we wait for `online` first)
 *  - missing context (no row ids) never triggers a retry; there is nothing to drain
 *  - missing status (never read yet) never triggers a retry; the mount effect will
 *    populate status and a subsequent reconnect will be evaluated normally
 *  - explicit blocker count > 0 always triggers a retry, even if the syncState is
 *    something other than 'offline'/'pending', because blocked entries are exactly
 *    what a reconnect should attempt to clear
 *  - syncState 'offline' or 'pending' triggers a retry; 'synced' (with no blockers)
 *    is a no-op
 */
export const shouldRetryProductionSyncOnReconnect = (args: {
  status: EntityBatchSyncStatus | null
  isOnline: boolean
  hasRowIds: boolean
}): boolean => {
  if (!args.isOnline) return false
  if (!args.hasRowIds) return false
  if (!args.status) return false
  if (args.status.blockedCount > 0) return true
  if (args.status.syncState === 'offline') return true
  if (args.status.syncState === 'pending') return true
  return false
}
